/**
 * 完了画面（出力ボタン群）の手続き。DownloadItem.vue の setup / methods から移設した。
 * 発火は mitt（emitter）、認証は useAuth に寄せてあるので Gate A / Infra 依存は増えない。
 * パネル SFC（CompletedOutputPanel.vue）に置くと 500 行を割るため composable に出している。
 */
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
  type Ref
} from 'vue'
import { useIntersectionObserver, useNow } from '@vueuse/core'
import axios from 'axios'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useDownloadItemAutoRt } from '@/composables/useDownloadItemAutoRt'
import { autoRtState } from '@/composables/useAutoRtSession'
import { useLoginPopupTracking } from '@/composables/format-edit/useLoginPopupTracking'
import { LOGIN_POPUP_ABANDON_REASON } from '@/utils/login-popup-tracking'
import { toLoginPopupTriggerButton } from '@/utils/format-edit/loginPopupTrigger'
import { resolveQuickEmailLabel } from '@/utils/format-edit/quickEmailLabelSchedule'
import {
  emitter,
  emitOpenLoginDialog,
  AUTO_RT,
  LOGIN_DIALOG_CLOSED,
  LOGIN_SUCCESS,
  UPDATE_RT_CONDITION
} from '@/plugins/emitter'

/** 完了画面のボタンが発火するイベント名（mitt → 未移行 hub は emitter.js が forward する） */
export type CompletedOutputEventName =
  'mail-PDF' | 'send-email-verification' | 'show-preview-4conv' | 'download-PDF'

const YAGI_EMAIL_EVENT = 'send-special-quickemail'

/** 「応募先にメール」（ヤギのメール便）を出さない書式。DownloadItem の v-if をそのまま移した */
const QUICK_EMAIL_EXCLUDED_FORMAT_IDS = [
  'rirekisho_design_001',
  'rirekisho_design_002',
  'shokumukeirekisho_with_image',
  'taishokutodoke',
  'atena'
]

/** 文言の切り替えは日付境界だけなので、1時間ごとの再評価で足りる */
const NOW_INTERVAL_MS = 60 * 60 * 1000

type UseCompletedOutputOptions = {
  formatId: Ref<string>
  isSendYagiEmailEnabled: Ref<boolean>
  /** 「応募先にメール」発火時に親へ無効化を返す（update:isSendYagiEmailEnabled） */
  onSendYagiEmail: () => void
}

export function useCompletedOutput(options: UseCompletedOutputOptions) {
  const { deviceLib, site, globalVar, varuna, gtm } = useHostServices()
  const { user, isAuthenticated } = useAuth()
  const loginPopupTracking = useLoginPopupTracking(gtm)
  const now = useNow({ interval: NOW_INTERVAL_MS })

  // 「応募先にメール」（ヤギのメール便）だけは 1 回きり。自分へのメール送信・端末DL は
  // 旧実装の本番分岐がローカル無効化を bind していない＝何度でも押せる挙動なので合わせる。
  const localIsSendYagiEmailEnabled = ref(options.isSendYagiEmailEnabled.value)
  const isAgree = ref(true)

  // 親はデータ変更時に prop を true へ戻して再送信を許可する
  watch(options.isSendYagiEmailEnabled, (value) => {
    localIsSendYagiEmailEnabled.value = value
  })

  // ログイン成功後に実行するイベント。画面に出す値ではないので ref にしない
  let pendingEvent: {
    eventName: CompletedOutputEventName
    additionalParams: Record<string, unknown>
  } | null = null
  let pendingYagiEmail = false

  const isEmailVerified = computed(() => user.value?.emailVerified === true)
  const isOnline = computed(() => deviceLib.isOnline)
  const isPdfDownloadButtonVisible = computed(() => site.appFlag !== 'b')
  const isQuickEmailVisible = computed(
    () =>
      !!globalVar.in_app_payments &&
      !QUICK_EMAIL_EXCLUDED_FORMAT_IDS.includes(options.formatId.value)
  )
  const quickEmailLabel = computed(
    () => resolveQuickEmailLabel(now.value).label
  )

  const autoRtVm = {
    $firebase: {
      get user() {
        const u = user.value
        return {
          uid: u?.uid ?? '',
          email: u?.email ?? '',
          // キャリコネの①②は ID トークンで認証する（無いと no_auth で出ない・送れない）
          getIdToken: () => (u ? u.getIdToken() : Promise.resolve(''))
        }
      },
      get isAuthenticated() {
        return isAuthenticated.value
      }
    },
    $deviceLib: deviceLib,
    $site: site,
    $axios: axios,
    $varuna: varuna,
    $globalVar: globalVar,
    get isAgree() {
      return isAgree.value
    },
    get formatId() {
      return options.formatId.value
    }
  } as unknown as Parameters<typeof useDownloadItemAutoRt>[0]

  const autoRt = useDownloadItemAutoRt(autoRtVm)
  const autoRtInfo = computed(() => autoRtState.info)

  function executeEvent(
    eventName: CompletedOutputEventName,
    additionalParams: Record<string, unknown> = {}
  ) {
    if (eventName === 'show-preview-4conv') {
      gtm.sendEprintEvent('cta_click', {
        format_id: options.formatId.value,
        click_location: 'download_item'
      })
    }

    if (eventName === 'send-email-verification') {
      emitter.emit(
        isEmailVerified.value ? 'mail-PDF' : 'send-email-verification',
        additionalParams
      )
      return
    }

    emitter.emit(eventName, additionalParams)
  }

  /** メール未確認ユーザー向け: 確認メールの送付だけを依頼する（送信ボタンは無効化しない） */
  function requestEmailVerification() {
    emitter.emit('send-email-verification', {})
  }

  function executeYagiEmailEvent() {
    localIsSendYagiEmailEnabled.value = false
    options.onSendYagiEmail()
    emitter.emit(YAGI_EMAIL_EVENT, options.formatId.value)
  }

  function openLoginDialogFor(eventName: string) {
    loginPopupTracking.trackOpen(
      toLoginPopupTriggerButton(eventName),
      options.formatId.value
    )
    emitOpenLoginDialog()
  }

  function handleEventWithLoginCheck(
    eventName: CompletedOutputEventName,
    additionalParams: Record<string, unknown> = {}
  ) {
    if (!isAuthenticated.value) {
      // 端末DL だけはログイン成功後に自動実行しない。会員登録直後は RT 条件が
      // まだ無く RT ポップアップを開けないまま端末DL画面へ遷移してしまうため、
      // 完了画面に留めてユーザーの再押下を待つ。
      pendingEvent =
        eventName === 'download-PDF' ? null : { eventName, additionalParams }
      openLoginDialogFor(eventName)
      return
    }
    executeEvent(eventName, additionalParams)
  }

  function handleYagiEmailClick() {
    if (!isAuthenticated.value) {
      pendingYagiEmail = true
      openLoginDialogFor(YAGI_EMAIL_EVENT)
      return
    }
    executeYagiEmailEvent()
  }

  function handleLoginSuccess() {
    loginPopupTracking.trackLoginSuccess()

    if (pendingYagiEmail) {
      pendingYagiEmail = false
      executeYagiEmailEvent()
    }

    if (pendingEvent) {
      const { eventName, additionalParams } = pendingEvent
      pendingEvent = null
      executeEvent(eventName, additionalParams)
    }
  }

  function handleLoginDialogClosed() {
    loginPopupTracking.trackAbandon(LOGIN_POPUP_ABANDON_REASON.dialogClose)
  }

  // mitt は可変長を配列1本で渡す（emitter.js の取り決め）
  const handleAutoRtMitt = (payload: unknown) => {
    const args = (payload ?? []) as Parameters<typeof autoRt.handleAutoRt>
    autoRt.handleAutoRt(...args)
  }
  const handleUpdateRtConditionMitt = (payload: unknown) => {
    const args = (payload ?? []) as Parameters<
      typeof autoRt.handleUpdateRtCondition
    >
    autoRt.handleUpdateRtCondition(...args)
  }

  onBeforeMount(() => {
    emitter.on(AUTO_RT, handleAutoRtMitt)
    emitter.on(UPDATE_RT_CONDITION, handleUpdateRtConditionMitt)
    emitter.on(LOGIN_SUCCESS, handleLoginSuccess)
    emitter.on(LOGIN_DIALOG_CLOSED, handleLoginDialogClosed)
  })

  onBeforeUnmount(() => {
    emitter.off(AUTO_RT, handleAutoRtMitt)
    emitter.off(UPDATE_RT_CONDITION, handleUpdateRtConditionMitt)
    emitter.off(LOGIN_SUCCESS, handleLoginSuccess)
    emitter.off(LOGIN_DIALOG_CLOSED, handleLoginDialogClosed)
  })

  // auto_rt バナーが出た（= client が決まった）タイミングで表示ログを送る
  watch(
    () => autoRtState.info.clientId,
    (clientId) => {
      if (!clientId) return
      autoRt.notifyDisplay(
        options.formatId.value,
        String(clientId),
        Number(autoRtState.info.permissionType) || 0
      )
    }
  )

  /** コンビニ印刷 CTA の表示計測。1回だけ送る */
  function observeEprintCtaImpression(target: Ref<HTMLElement | null>) {
    const { stop } = useIntersectionObserver(
      target,
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        stop()
        gtm.sendEprintEvent('cta_impression', {
          format_id: options.formatId.value,
          impression_location: 'download_item'
        })
      },
      { threshold: 0.5 }
    )
  }

  function onRtAgreeChange(agreed: boolean) {
    isAgree.value = agreed
    autoRt.setAgreed(agreed)
  }

  return {
    isAuthenticated,
    isEmailVerified,
    isOnline,
    isPdfDownloadButtonVisible,
    isQuickEmailVisible,
    quickEmailLabel,
    localIsSendYagiEmailEnabled,
    pdfMailBtnId: autoRt.pdfMailBtnId,
    pdfDownloadBtnId: autoRt.pdfDownloadBtnId,
    autoRtInfo,
    hasAutoRtPartnerTerms: autoRt.hasAutoRtPartnerTerms,
    autoRtPartnerTermsHtml: autoRt.autoRtPartnerTermsHtml,
    isAgree,
    handleEventWithLoginCheck,
    handleYagiEmailClick,
    requestEmailVerification,
    observeEprintCtaImpression,
    onRtAgreeChange
  }
}
